import { language, t } from './i18n';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, onSessionLost, tokens } from './api';
import { detachPush, resyncPush } from './push';
import type { AuthResponse, SessionUser } from './types';

/**
 * Session, theme and toasts.
 *
 * The session is restored from the stored refresh token on boot rather than
 * trusted from localStorage: the access token may well have expired while the tab
 * was closed, and `GET /auth/me` is the only thing that actually proves the
 * session is still alive (it also picks up a role or KYC change made elsewhere).
 */

export type Toast = { id: number; message: string; tone: 'info' | 'ok' | 'error' };

type AppContextValue = {
  user: SessionUser | null;
  loadingSession: boolean;
  isHost: boolean;
  isAdmin: boolean;
  signIn: (response: AuthResponse) => void;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  toasts: Toast[];
  toast: (message: string, tone?: Toast['tone']) => void;
  /** Turns an unknown throw into a toast, and returns the ApiError when it was one. */
  reportError: (error: unknown, fallback?: string) => ApiError | null;
};

const AppContext = createContext<AppContextValue | null>(null);

const THEME_KEY = 'bedly.theme';

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const toastId = useRef(0);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const toast = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4200);
  }, []);

  const reportError = useCallback(
    (error: unknown, fallback = t('Une erreur est survenue')) => {
      if (error instanceof ApiError) {
        toast(error.message, 'error');
        return error;
      }
      toast(fallback, 'error');
      return null;
    },
    [toast],
  );

  const refreshUser = useCallback(async () => {
    if (!tokens.access() && !tokens.refresh()) {
      setUser(null);
      return;
    }
    try {
      const response = await api.get<{ user: SessionUser }>('/auth/me');
      setUser(response.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refreshUser();
      if (!cancelled) setLoadingSession(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  useEffect(
    () =>
      onSessionLost(() => {
        setUser(null);
        toast(t('Votre session a expiré. Reconnectez-vous.'), 'error');
      }),
    [toast],
  );

  const signIn = useCallback((response: AuthResponse) => {
    tokens.set(response.tokens.accessToken, response.tokens.refreshToken);
    setUser(response.user);
    // The account follows the language the person is using now, so emails,
    // the payment page and notifications arrive in it.
    if (response.user.locale !== language) {
      void api.patch('/users/me', { locale: language }).catch(() => undefined);
    }
    // A device already allowed to notify now notifies this person.
    void resyncPush();
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = tokens.refresh();
    // While still signed in: this device stops receiving their notifications.
    await detachPush();
    try {
      await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
    } catch {
      // Logging out locally must succeed even if the call does not.
    }
    tokens.clear();
    setUser(null);
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      user,
      loadingSession,
      isHost: user?.roles.includes('HOST') ?? false,
      isAdmin: user?.roles.includes('ADMIN') ?? false,
      signIn,
      signOut,
      refreshUser,
      theme,
      toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
      toasts,
      toast,
      reportError,
    }),
    [user, loadingSession, signIn, signOut, refreshUser, theme, toasts, toast, reportError],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside AppProvider');
  return context;
}
